import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  resolveMapsLink,
  importTeamLogo,
  clearMapsAutofill,
} from "../../Redux/Reducers/mapsAutofillReducer";
import { ALERT_TEXT, MAPS_WARNING_TEXT } from "../../constants/titles";
import {
  MAPS_TYPES,
  isGoogleMapsLink,
  mapTeamDraftToFormValues,
  mapVenueDraftToFormValues,
  dataUriToBase64,
} from "../../utils/mapsAutofill";

const useStyles = makeStyles({
  wrapper: {
    minWidth: "440px",
    "@media (max-width: 600px)": { minWidth: "280px" },
  },
  note: {
    fontSize: "12px",
    color: "#667085",
    marginTop: "8px",
  },
  section: {
    marginTop: "16px",
  },
  warning: {
    fontSize: "12px",
    color: "#B54708",
    marginTop: "4px",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "8px",
  },
  logoImage: {
    maxWidth: "96px",
    maxHeight: "96px",
    objectFit: "contain",
    border: "1px solid #EAECF0",
    borderRadius: "4px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "20px",
  },
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "16px",
  },
});

const MODES = {
  TEAM_ONLY: "TEAM_ONLY",
  TEAM_AND_VENUE: "TEAM_AND_VENUE",
};

/**
 * Paste a Google Maps link and turn it into pre-filled create form(s).
 *
 * Rendered as the body of a ConfirmDialog (same composition as
 * QualifyingScoreCloneDialog). It never creates anything itself — it resolves the
 * link, lets the admin review the proposed logo, then hands the drafts back through
 * `onApply` for the page to prefill its existing create form.
 */
const CreateFromLinkDialog = ({ open, type, onCancel, onApply }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { resolving, importingLogo } = useSelector(
    (state) => state.mapsAutofill,
  );

  const [link, setLink] = useState("");
  const [mode, setMode] = useState(MODES.TEAM_ONLY);
  const [linkError, setLinkError] = useState("");
  const [resolved, setResolved] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [keepLogo, setKeepLogo] = useState(true);
  const [logoIndex, setLogoIndex] = useState(-1); // -1 = the primary candidate

  const isTeamPage = type === MAPS_TYPES.TEAM;

  useEffect(() => {
    if (open) return;

    setLink("");
    setMode(MODES.TEAM_ONLY);
    setLinkError("");
    setResolved(null);
    setWarnings([]);
    setKeepLogo(true);
    setLogoIndex(-1);
    dispatch(clearMapsAutofill());
  }, [open, dispatch]);

  // The primary candidate plus any alternates, as one addressable list.
  const logoCandidates = (() => {
    const logo = resolved?.logo;
    if (!logo?.candidateUrl) return [];

    const primary = {
      url: logo.candidateUrl,
      width: logo.width,
      height: logo.height,
    };
    const alternates = Array.isArray(logo.alternates) ? logo.alternates : [];

    return [primary, ...alternates.filter((a) => a?.url)];
  })();

  const activeLogo =
    logoCandidates.length > 0
      ? logoCandidates[logoIndex === -1 ? 0 : logoIndex] || logoCandidates[0]
      : null;

  const handleResolve = async () => {
    const trimmed = link.trim();

    if (!trimmed) {
      setLinkError("Paste a Google Maps link");
      return;
    }
    // Mirror the backend's host allow-list so an obvious mistake fails instantly.
    if (!isGoogleMapsLink(trimmed)) {
      setLinkError("That doesn't look like a Google Maps link");
      return;
    }

    setLinkError("");

    const includeVenue = isTeamPage && mode === MODES.TEAM_AND_VENUE;

    const action = await dispatch(
      resolveMapsLink({ link: trimmed, type, includeVenue }),
    );

    if (resolveMapsLink.rejected.match(action)) {
      setLinkError(action.payload || ALERT_TEXT.MAPS_RESOLVE_FAILED);
      return;
    }

    const payload = action.payload || {};
    const nextWarnings = Array.isArray(payload.warnings)
      ? [...payload.warnings]
      : [];

    // The venue form's timezone select is a fixed list and silently falls back to the
    // browser zone on an unknown id, so flag anything it can't represent.
    const venuePreview = payload.venue
      ? mapVenueDraftToFormValues(payload.venue)
      : null;

    if (
      venuePreview?.timezoneUnsupported &&
      !nextWarnings.includes("timezone_not_determined")
    ) {
      nextWarnings.push("timezone_unsupported");
    }

    setResolved(payload);
    setWarnings(nextWarnings);
    setLogoIndex(-1);
    setKeepLogo(Boolean(payload?.logo?.candidateUrl));
  };

  const handleApply = async () => {
    if (!resolved) return;

    const includeVenue = isTeamPage && mode === MODES.TEAM_AND_VENUE;

    const teamValues = resolved.team
      ? mapTeamDraftToFormValues(resolved.team)
      : null;

    const venueMapped = resolved.venue
      ? mapVenueDraftToFormValues(resolved.venue)
      : null;

    let logo = null;

    // Only import when the admin kept a candidate — the endpoint downloads the image.
    if (isTeamPage && keepLogo && activeLogo?.url) {
      const action = await dispatch(
        importTeamLogo({ imageUrl: activeLogo.url }),
      );

      if (importTeamLogo.fulfilled.match(action) && action.payload?.logoUrl) {
        logo = {
          // FileForm stores the bare base64 body in `cloudAlias`, and getFillDtoTeam
          // derives logoUrl from it. Strip the data-URI prefix so the submitted
          // payload is identical to a manual upload.
          cloudAlias: dataUriToBase64(action.payload.logoUrl),
          previewUrl: action.payload.logoUrl,
        };
      } else {
        // Non-fatal: fall through with no logo and let the admin upload manually.
        setWarnings((prev) =>
          prev.includes("logo_import_failed")
            ? prev
            : [...prev, "logo_import_failed"],
        );
      }
    }

    // The venue form falls back to the BROWSER's timezone when it has no usable value,
    // which looks plausible but is very likely wrong for the venue. This dialog is about
    // to close, so raise it as a toast that survives into the form the admin lands on.
    const timezoneNeedsAttention =
      venueMapped?.timezoneUnsupported ||
      warnings.includes("timezone_not_determined");

    if (venueMapped && timezoneNeedsAttention) {
      toast.warn(
        venueMapped.timezoneUnsupported
          ? MAPS_WARNING_TEXT.timezone_unsupported
          : MAPS_WARNING_TEXT.timezone_not_determined,
      );
    }

    onApply({
      type,
      includeVenue,
      teamValues,
      venueValues: venueMapped?.values || null,
      timezoneUnsupported: Boolean(venueMapped?.timezoneUnsupported),
      logo,
    });
  };

  if (!open) return null;

  const busy = resolving || importingLogo;

  return (
    <Box className={classes.wrapper}>
      <TextField
        label="Google Maps link"
        placeholder="https://maps.app.goo.gl/..."
        variant="outlined"
        size="small"
        fullWidth
        autoFocus
        value={link}
        onChange={(e) => {
          setLink(e.target.value);
          if (linkError) setLinkError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !busy) handleResolve();
        }}
        error={Boolean(linkError)}
        helperText={linkError}
        disabled={busy}
      />

      {isTeamPage ? (
        <FormControl component="fieldset" className={classes.section}>
          <Typography variant="subtitle2">What should we create?</Typography>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <FormControlLabel
              value={MODES.TEAM_ONLY}
              control={<Radio size="small" />}
              label="Team only"
              disabled={busy}
            />
            <FormControlLabel
              value={MODES.TEAM_AND_VENUE}
              control={<Radio size="small" />}
              label="Team and venue"
              disabled={busy}
            />
          </RadioGroup>
        </FormControl>
      ) : null}

      {resolving ? (
        <Box className={classes.progressRow}>
          <CircularProgress size={18} />
          <Typography className={classes.note}>
            Reading the link from Google — this can take a few seconds.
          </Typography>
        </Box>
      ) : null}

      {resolved ? (
        <Box className={classes.section}>
          <Typography variant="subtitle2">
            {resolved.team?.name || resolved.venue?.poolName || "Place found"}
          </Typography>
          <Typography className={classes.note}>
            {[
              resolved.team?.streetOne || resolved.venue?.streetOne,
              resolved.team?.city || resolved.venue?.city,
              resolved.team?.state || resolved.venue?.state,
              resolved.team?.postalCode || resolved.venue?.postalCode,
            ]
              .filter(Boolean)
              .join(", ")}
          </Typography>

          {warnings.map((warning) => (
            <Typography key={warning} className={classes.warning}>
              {MAPS_WARNING_TEXT[warning] || warning}
            </Typography>
          ))}

          {isTeamPage && activeLogo ? (
            <Box className={classes.section}>
              <Typography variant="subtitle2">Proposed logo</Typography>
              <Box className={classes.logoRow}>
                <img
                  src={activeLogo.url}
                  alt="proposed logo"
                  className={classes.logoImage}
                />
                <Box>
                  <Typography className={classes.note}>
                    {activeLogo.width && activeLogo.height
                      ? `${activeLogo.width} × ${activeLogo.height}`
                      : "Dimensions unknown"}
                  </Typography>
                  <Box style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                    {logoCandidates.length > 1 ? (
                      <Button
                        size="small"
                        onClick={() =>
                          setLogoIndex((prev) => {
                            const current = prev === -1 ? 0 : prev;
                            return (current + 1) % logoCandidates.length;
                          })
                        }
                        disabled={busy}
                      >
                        Try another
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      onClick={() => setKeepLogo((prev) => !prev)}
                      disabled={busy}
                    >
                      {keepLogo ? "Don't use this logo" : "Use this logo"}
                    </Button>
                  </Box>
                  {!keepLogo ? (
                    <Typography className={classes.note}>
                      You can upload a logo on the next screen.
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Box className={classes.actions}>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>

        {resolved ? (
          <Button
            color="primary"
            variant="contained"
            onClick={handleApply}
            disabled={busy}
          >
            {importingLogo ? "Importing logo..." : "Review & continue"}
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            onClick={handleResolve}
            disabled={busy || !link.trim()}
          >
            Look up
          </Button>
        )}
      </Box>
    </Box>
  );
};

CreateFromLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf([MAPS_TYPES.TEAM, MAPS_TYPES.VENUE]).isRequired,
  onCancel: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default CreateFromLinkDialog;
